import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/authStore';
import { API_BASE_URL, API_V2_BASE_URL } from '../config/environment';

// Create axios instance for v1 API
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 100000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create axios instance for v2 API
const apiV2: AxiosInstance = axios.create({
  baseURL: API_V2_BASE_URL,
  timeout: 100000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().token;
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect to login if it's an authenticated endpoint (not login itself)
    if (error.response?.status === 401 && 
        error.config?.url !== '/api/auth/login' && 
        !error.config?.url?.includes('/api/auth/')) {
      useAuthStore.getState().logout();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Request interceptor for v2 API to add auth token
apiV2.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().token;
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for v2 API to handle auth errors
apiV2.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect to login if it's an authenticated endpoint (not login itself)
    if (error.response?.status === 401 && 
        !error.config?.url?.includes('/v2/auth/')) {
      useAuthStore.getState().logout();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API calls
export const authAPI = {
  login: (credentials: { email: string; password: string; rememberMe?: boolean }) =>
    api.post('/api/auth/login', credentials),
    
  register: (data: { 
    companyName: string; 
    adminName: string; 
    email: string; 
    password: string; 
    confirmPassword: string; 
    quota: number 
  }) =>
    api.post('/api/auth/register', data),
    
  forgotPassword: (email: string) =>
    api.post('/api/auth/forgot-password', { email }),
    
  resetPassword: (data: { token: string; password: string; confirmPassword: string }) =>
    api.post('/api/auth/reset-password', data),
    
  logout: () =>
    api.post('/api/auth/logout'),
};

// Client API calls
export const clientAPI = {
  getClients: () =>
    api.get('/api/clients'),
    
  selectClient: (clientId: string) =>
    api.post('/api/clients/select', { clientId }),
    
  impersonateClientAdmin: (clientId: string) =>
    api.post('/api/clients/impersonate', { clientId }),
    
  stopImpersonation: () =>
    api.post('/api/clients/stop-impersonation'),
    
  // Client management endpoints (Day 2)
  createClient: (clientData: { name: string; domain?: string; description?: string }) =>
    api.post('/api/clients', clientData),
    
  updateClient: (clientId: string, updateData: { name?: string; status?: string; domain?: string; description?: string }) =>
    api.patch(`/api/clients/${clientId}`, updateData),
    
  getClient: (clientId: string) =>
    api.get(`/api/clients/${clientId}`),
    
  getClientStats: (clientId: string) =>
    api.get(`/api/clients/${clientId}/stats`),
    
  deleteClient: (clientId: string) =>
    api.delete(`/api/clients/${clientId}`),
    
  getMyClients: () =>
    api.get('/api/clients/my-clients'),
};

// User API calls
export const userAPI = {
  getProfile: () =>
    api.get('/api/user/profile'),
    
  updateProfile: (data: any) =>
    api.put('/api/user/profile', data),
    
  // User management endpoints (Day 2)
  getUsers: (filters?: { email?: string; role?: string; status?: string; client_id?: string; limit?: number }) =>
    api.get('/api/users', { params: filters }),
    
  createUser: (userData: { email: string; password: string; role: string; name?: string; status?: string; client_id?: string }) =>
    api.post('/api/users', userData),
    
  updateUser: (userId: string, updateData: { role?: string; status?: string; name?: string }) =>
    api.patch(`/api/users/${userId}`, updateData),
    
  deleteUser: (userId: string) =>
    api.delete(`/api/users/${userId}`),
    
  getUser: (userId: string) =>
    api.get(`/api/users/${userId}`),
    
  // User invitation endpoints
  inviteUser: (inviteData: { email: string; role: string; name?: string; client_id?: string }) =>
    api.post('/api/users/invite', inviteData),
    
  acceptInvitation: (acceptData: { token: string; password: string; confirmPassword: string }) =>
    api.post('/api/users/accept-invite', acceptData),
};

// Document Types API calls
export const documentTypesAPI = {
  // Get all document types
  getDocumentTypes: (activeOnly: boolean = true) =>
    api.get('/api/document-types', { params: { active_only: activeOnly } }),
    
  // Get specific document type
  getDocumentType: (documentType: string) =>
    api.get(`/api/document-types/${documentType}`),
    
  // Create new document type
  createDocumentType: (data: {
    document_type: string;
    description: string;
    schema: {
      version: string;
      fields: Array<{
        name: string;
        type: 'string' | 'number' | 'date' | 'boolean' | 'email' | 'phone' | 'currency';
        required: boolean;
        description?: string;
        validation_pattern?: string;
        extraction_rules?: {
          patterns?: string[];
          keywords?: string[];
          region?: { x: number; y: number; width: number; height: number };
        };
      }>;
    };
    settings?: {
      ocr_engine?: string;
      confidence_threshold?: number;
      auto_correct?: boolean;
      review_required?: boolean;
    };
  }) =>
    api.post('/api/document-types', data),
    
  // Update document type
  updateDocumentType: (documentType: string, data: any) =>
    api.patch(`/api/document-types/${documentType}`, data),
    
  // Delete document type (soft delete)
  deleteDocumentType: (documentType: string) =>
    api.delete(`/api/document-types/${documentType}`),
    
  // Clone document type
  cloneDocumentType: (data: {
    source_document_type: string;
    target_document_type: string;
    description?: string;
    copy_settings?: boolean;
  }) =>
    api.post('/api/document-types/clone', data),
    
  // Import document type from JSON
  importDocumentType: (data: { schema: any; overwrite?: boolean }) =>
    api.post('/api/document-types/import', data),
    
  // Export document type as JSON
  exportDocumentType: (documentType: string) =>
    api.get(`/api/document-types/${documentType}/export`),
    
  // Test document type with sample data
  testDocumentType: (documentType: string, data: {
    test_data?: any;
    file?: File;
    validate_only?: boolean;
  }) =>
    api.post(`/api/document-types/${documentType}/test`, data),
};

// Application Management API calls
export const applicationAPI = {
  // Get company quota
  getCompanyQuota: () =>
    api.get('/api/company/quota'),
    
  // Update company quota (admin only)
  updateCompanyQuota: (totalQuota: number) =>
    api.put('/api/company/quota', { total_quota: totalQuota }),
    
  // Get all applications
  getApplications: (params?: { status?: string; limit?: number }) =>
    api.get('/api/applications', { params }),
    
  // Create new application
  createApplication: (data: {
    name: string;
    description: string;
    quota_limit: number;
    rate_limit_per_minute?: number;
  }) =>
    api.post('/api/applications', data),
    
  // Get specific application
  getApplication: (appId: string) =>
    api.get(`/api/applications/${appId}`),
    
  // Update application
  updateApplication: (appId: string, data: {
    name?: string;
    description?: string;
    quota_limit?: number;
    rate_limit_per_minute?: number;
    status?: string;
  }) =>
    api.put(`/api/applications/${appId}`, data),
    
  // Update application status (activate/deactivate)
  updateApplicationStatus: (appId: string, status: string) =>
    api.put(`/api/applications/${appId}/status`, { status }),
    
  // Create API key for application
  createApiKey: (appId: string, data: {
    name: string;
    description?: string;
  }) =>
    api.post(`/api/applications/${appId}/api-keys`, data),
    
  // Get API keys for application
  getApiKeys: (appId: string) =>
    api.get(`/api/applications/${appId}/api-keys`),
    
  // Update API key
  updateApiKey: (keyId: string, data: {
    name?: string;
    description?: string;
    status?: string;
  }) =>
    api.put(`/api/api-keys/${keyId}`, data),
    
  // Revoke API key
  revokeApiKey: (keyId: string) =>
    api.post(`/api/api-keys/${keyId}/revoke`),
    
  // Get application analytics
  getAnalytics: (appId: string, params?: { days?: number; format?: string }) =>
    api.get(`/api/applications/${appId}/analytics`, { params }),
    
  // Get integration guide
  getIntegrationGuide: (appId: string) =>
    api.get(`/api/applications/${appId}/integration-guide`),
    
  // Get OCR request logs for application
  getLogs: (appId: string, params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    status?: string[];
    document_type?: string[];
    date_from?: string;
    date_to?: string;
    api_key_id?: string;
    confidence_min?: number;
  }) =>
    api.get(`/api/applications/${appId}/logs`, { 
      params,
      paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
          if (Array.isArray(value)) {
            // Send array parameters multiple times with the same key (without brackets)
            value.forEach(item => searchParams.append(key, item));
          } else if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        }
        return searchParams.toString();
      }
    }),
    
  // Get specific OCR request details
  getRequestDetails: (requestId: string) =>
    api.get(`/api/ocr-requests/${requestId}`),
    
  // Export logs
  exportLogs: (appId: string, params?: {
    format?: 'csv' | 'json' | 'xlsx';
    request_ids?: string[];
    filters?: any;
  }) =>
    api.post(`/api/applications/${appId}/logs/export`, params, {
      responseType: 'blob'
    }),
    
  // Get global logs across all applications
  getGlobalLogs: (params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    status?: string[];
    document_type?: string[];
    application_id?: string[];
    date_range?: [string, string];
    min_confidence?: number;
    max_confidence?: number;
    sort_by?: string;
    sort_order?: string;
  }) => {
    // Convert array parameters to proper format for backend
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // For arrays, append each value with the same key
          value.forEach(v => queryParams.append(key, v));
        } else if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
    }
    
    return api.get(`/api/logs/global?${queryParams.toString()}`);
  },
    
  // Get global logs statistics
  getGlobalLogsStats: (params?: {
    days?: number;
  }) =>
    api.get('/api/logs/global/stats', { params }),
    
  // Export global logs
  exportGlobalLogs: (params?: {
    format?: 'csv' | 'json' | 'xlsx';
    request_ids?: string[];
    filters?: any;
  }) =>
    api.post('/api/logs/global/export', params, {
      responseType: 'blob'
    }),
    
  // Regenerate signed URL for application log image
  regenerateApplicationLogImageUrl: (appId: string, requestId: string, params?: {
    expiration_hours?: number;
  }) =>
    api.post(`/api/applications/${appId}/logs/${requestId}/regenerate-image-url`, params),
    
  // Regenerate signed URL for global log image
  regenerateGlobalLogImageUrl: (requestId: string, params?: {
    expiration_hours?: number;
  }) =>
    api.post(`/api/logs/global/${requestId}/regenerate-image-url`, params),
};

// Dashboard API calls
export const dashboardAPI = {
  getDashboardData: () =>
    api.get('/api/dashboard'),
    
  getQuickStats: () =>
    api.get('/api/dashboard/quick-stats'),
};

// Analytics API calls
export const analyticsAPI = {
  // Get company-wide analytics
  getCompanyAnalytics: (params?: { 
    date_filter?: string;
    format?: string;
  }) =>
    apiV2.get('/v2/analytics/company/comprehensive', { params }),
    
  // Get application-specific analytics (enhanced with custom date support)
  getApplicationAnalytics: (applicationId: string, params?: { 
    date_filter?: string;
    start_date?: string;
    end_date?: string;
    format?: string;
  }) =>
    apiV2.get(`/v2/analytics/application/${applicationId}/comprehensive`, { params }),
    
  // Get application analytics with simplified date filtering
  getApplicationAnalyticsSimple: (applicationId: string, params?: {
    days?: number;
    start_date?: string;
    end_date?: string;
  }) =>
    apiV2.get(`/v2/analytics/application/${applicationId}/analytics`, { params }),
    
  // Get dashboard summary analytics
  getDashboardSummary: (params?: { 
    date_filter?: string;
  }) =>
    apiV2.get('/v2/analytics/dashboard/summary', { params }),
    
  // Get interactive chart data
  getInteractiveCharts: (params?: { 
    chart_type?: 'success_failure' | 'document_types' | 'applications' | 'time_trends';
    date_filter?: string;
    application_id?: string;
  }) =>
    apiV2.get('/v2/analytics/charts/interactive', { params }),
    
  // Export analytics data
  exportAnalytics: (params?: {
    format?: 'json' | 'csv';
    date_filter?: string;
    application_id?: string;
    include_details?: boolean;
  }) =>
    apiV2.get('/v2/analytics/export/comprehensive', { 
      params,
      responseType: params?.format === 'csv' ? 'blob' : 'json'
    }),
};

// Role API calls
export const roleAPI = {
  getRoles: () =>
    api.get('/api/roles'),
    
  getAvailablePermissions: () =>
    api.get('/api/roles/permissions'),
    
  createRole: (data: {
    role_name: string;
    description: string;
    permissions: string[];
    color?: string;
  }) =>
    api.post('/api/roles', data),
    
  getRole: (roleId: string) =>
    api.get(`/api/roles/${roleId}`),
    
  updateRole: (roleId: string, data: {
    description?: string;
    permissions?: string[];
    color?: string;
  }) =>
    api.put(`/api/roles/${roleId}`, data),
    
  deleteRole: (roleId: string) =>
    api.delete(`/api/roles/${roleId}`),
};

// Ultra-fast App OCR API calls (uses Redis cache)
export const appOcrAPI = {
  // Process OCR with API key - ultra fast
  processOCR: (formData: FormData) => 
    api.post('/v2/app-ocr/process', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    
  // Get document types from Redis cache - ULTRA FAST
  getDocumentTypesCached: (apiKey: string) =>
    api.get('/v2/app-ocr/document-types', { params: { api_key: apiKey } }),
    
  // Test network connection - instant ping
  ping: () =>
    api.get('/v2/app-ocr/ping'),
    
  // Get health status
  getHealth: () =>
    api.get('/v2/app-ocr/health'),
    
  // Refresh Redis cache
  refreshCache: () =>
    api.post('/v2/app-ocr/cache/refresh'),
    
  // Get cache status
  getCacheStatus: () =>
    api.get('/v2/app-ocr/cache/status'),
    
  // Regenerate signed URL for API v2 OCR request image
  regenerateImageUrl: (requestId: string, params?: {
    expiration_hours?: number;
  }) =>
    apiV2.post(`/v2/ocr/requests/${requestId}/regenerate-image-url`, {}, { params }),
};

// Billing API calls
export const billingAPI = {
  // Get full billing overview (balance, transactions, plan, alerts)
  getBillingOverview: (params?: { limit?: number }) =>
    api.get('/api/billing/overview', { params }),

  // Get credit balance
  getBalance: () =>
    api.get('/api/billing/balance'),

  // Get transaction history
  getTransactions: (params?: { limit?: number; offset?: number }) =>
    api.get('/api/billing/transactions', { params }),

  // Get current plan
  getPlan: () =>
    api.get('/api/billing/plan'),

  // Get topup redirect URL (redirects to Odoo)
  getTopupRedirect: () =>
    api.get('/api/billing/topup-redirect'),

  // Get alert preferences
  getAlertPreferences: () =>
    api.get('/api/billing/alert-preferences'),

  // Update alert preferences
  updateAlertPreferences: (preferences: {
    email_alerts_enabled?: boolean;
    threshold_75_enabled?: boolean;
    threshold_50_enabled?: boolean;
    threshold_25_enabled?: boolean;
    threshold_5_enabled?: boolean;
    threshold_0_enabled?: boolean;
  }) =>
    api.put('/api/billing/alert-preferences', preferences),

  // Get webhook settings
  getWebhookSettings: () =>
    api.get('/api/billing/webhook-settings'),
};

// Default export for backward compatibility
const api_default = api;
export default api_default;
